import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Mail, Database, Lock, UserCheck, Trash2, Globe } from "lucide-react";
import { CardWatchMark } from "@/components/CardWatchLogo";

const Section = ({ id, icon: Icon, title, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`privacy-section-${id}`}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-[#00E676]/[0.08] border border-[#00E676]/25 flex items-center justify-center">
        <Icon size={17} className="text-[#00E676]" />
      </div>
      <h2
        className="text-white text-2xl font-semibold tracking-tight"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {title}
      </h2>
    </div>
    <div className="text-slate-300 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

const InlineTag = ({ children }) => (
  <span className="text-white font-medium">{children}</span>
);

const Privacy = () => {
  const lastUpdated = "12 August 2026";

  const toc = [
    { id: "who", label: "Who we are" },
    { id: "data", label: "What we collect" },
    { id: "processors", label: "Third-party processors" },
    { id: "purpose", label: "Why we collect it" },
    { id: "retention", label: "Retention" },
    { id: "security", label: "Security" },
    { id: "rights", label: "Your rights" },
    { id: "contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen bg-[#060B19] text-white relative overflow-hidden">
      <div className="pointer-events-none fixed -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00E676]/[0.06] blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/25 blur-[160px]" />

      <header className="relative z-10 bg-[#060B19]/70 backdrop-blur-xl border-b border-white/5">
        <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-3 group"
            data-testid="privacy-back-home"
          >
            <ArrowLeft
              size={16}
              className="text-slate-500 group-hover:text-white transition-colors"
            />
            <div className="flex items-center gap-3">
              <CardWatchMark size={30} />
              <span
                className="text-white font-semibold tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16 }}
              >
                Card<span style={{ color: "#00E676" }}>Watch</span>
              </span>
            </div>
          </Link>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
            Privacy Policy
          </div>
        </div>
      </header>

      <main className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#00E676]/30 bg-[#00E676]/[0.06] px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
            <ShieldCheck size={11} /> Your data, your rules
          </div>
          <h1
            className="mt-6 text-white text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="privacy-title"
          >
            Privacy Policy
          </h1>
          <p className="mt-4 text-slate-400 text-base">
            Last updated · <InlineTag>{lastUpdated}</InlineTag>
          </p>
          <p className="mt-6 text-slate-300 text-[15px] leading-relaxed">
            CardWatch is a personal finance dashboard that helps you track credit card
            balances, due dates and utilization. We take the privacy of your financial
            data seriously. This policy explains, in plain English, exactly what we
            collect, why, and the rights you have over it.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-[220px_1fr] gap-10">
          {/* Table of contents */}
          <nav
            className="lg:sticky lg:top-24 self-start rounded-2xl border border-white/5 bg-white/[0.02] p-5"
            data-testid="privacy-toc"
          >
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-bold mb-3">
              Contents
            </div>
            <ul className="space-y-2 text-sm">
              {toc.map((t) => (
                <li key={t.id}>
                  <a
                    href={`#${t.id}`}
                    className="block text-slate-400 hover:text-white transition-colors py-1"
                    data-testid={`privacy-toc-${t.id}`}
                  >
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="space-y-14 max-w-3xl">
            <Section id="who" icon={UserCheck} title="1 · Who we are">
              <p>
                CardWatch (“<InlineTag>we</InlineTag>”, “<InlineTag>us</InlineTag>”) is
                the operator of the CardWatch web and Android application, available at{" "}
                <InlineTag>https://cardwatchapp.com</InlineTag>. For the purposes of the
                UK GDPR and EU GDPR, CardWatch is the <InlineTag>data controller</InlineTag>{" "}
                of any personal data you provide directly to us.
              </p>
            </Section>

            <Section id="data" icon={Database} title="2 · What we collect">
              <p>We deliberately collect as little as possible. In practice, that means:</p>
              <ul className="list-disc pl-5 space-y-2 marker:text-[#00E676]">
                <li>
                  <InlineTag>Account &amp; settings</InlineTag> — a unique user id, your
                  chosen home currency, the reminder email address you type in Settings,
                  and whether reminders are switched on.
                </li>
                <li>
                  <InlineTag>Credit-card metadata (manual entry)</InlineTag> — nickname,
                  issuer, the last four digits, statement balance, credit limit and due
                  date. We <InlineTag>never</InlineTag> ask for or store full PANs, CVVs,
                  expiry dates or PINs.
                </li>
                <li>
                  <InlineTag>Open banking data (Plaid)</InlineTag> — if you choose to
                  link a card via Plaid, we receive read-only account balances,
                  currency and recent transactions. We do <InlineTag>not</InlineTag>{" "}
                  receive your bank login credentials.
                </li>
                <li>
                  <InlineTag>Reminder logs</InlineTag> — timestamps and the number of
                  cards included in each reminder we send you, plus the Resend delivery
                  id for audit.
                </li>
                <li>
                  <InlineTag>Basic product analytics</InlineTag> — anonymised page views
                  and interaction events to understand which features you actually use.
                </li>
              </ul>
              <p>
                We do <InlineTag>not</InlineTag> sell, rent or share your personal or
                financial data with advertisers.
              </p>
            </Section>

            <Section id="processors" icon={Globe} title="3 · Third-party processors">
              <p>
                We use a small number of carefully-chosen sub-processors to run the app.
                Each one is bound by a data processing agreement and only receives the
                minimum data needed to do its job.
              </p>
              <div className="mt-2 rounded-xl border border-white/5 bg-white/[0.02] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.18em] text-slate-500 bg-white/[0.02]">
                    <tr>
                      <th className="text-left px-4 py-3">Processor</th>
                      <th className="text-left px-4 py-3">Purpose</th>
                      <th className="text-left px-4 py-3">Region</th>
                    </tr>
                  </thead>
                  <tbody className="[&>tr]:border-t [&>tr]:border-white/5 text-slate-300">
                    <tr>
                      <td className="px-4 py-3 text-white font-medium">Plaid</td>
                      <td className="px-4 py-3">
                        Regulated open banking — read-only account and transaction
                        data
                      </td>
                      <td className="px-4 py-3">US / EU / UK</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white font-medium">Resend</td>
                      <td className="px-4 py-3">
                        Delivery of payment-due reminder emails from
                        alerts@cardwatchapp.com
                      </td>
                      <td className="px-4 py-3">EU (eu-west-1)</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white font-medium">MongoDB Atlas</td>
                      <td className="px-4 py-3">
                        Encrypted storage of your settings, manual cards and reminder logs
                      </td>
                      <td className="px-4 py-3">EU</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white font-medium">PostHog</td>
                      <td className="px-4 py-3">
                        Anonymised product analytics — no bank data or emails leave the
                        app
                      </td>
                      <td className="px-4 py-3">EU</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Section>

            <Section id="purpose" icon={ShieldCheck} title="4 · Why we collect it">
              <p>
                We only use your data to run CardWatch for you. The specific purposes are:
              </p>
              <ul className="list-disc pl-5 space-y-2 marker:text-[#00E676]">
                <li>Show your live balances, utilization and spending trend</li>
                <li>Send the payment-due reminder emails you asked for</li>
                <li>Enforce your plan limits (Free vs Pro card cap)</li>
                <li>
                  Debug crashes and improve the app based on anonymised product analytics
                </li>
              </ul>
              <p>
                Lawful basis under UK/EU GDPR: <InlineTag>Contract</InlineTag> (delivering
                the service you signed up for) and <InlineTag>Legitimate interest</InlineTag>{" "}
                (product analytics, security and fraud prevention).
              </p>
            </Section>

            <Section id="retention" icon={Trash2} title="5 · How long we keep it">
              <p>
                Manual cards, settings and Plaid access tokens are kept for as long as your
                account is active. When you delete a card, it is deleted from our
                database immediately. When you delete your account, we purge every piece
                of personal data linked to it within <InlineTag>30 days</InlineTag>.
                Reminder-delivery logs are retained for 12 months for compliance and then
                automatically removed.
              </p>
            </Section>

            <Section id="security" icon={Lock} title="6 · How we secure it">
              <ul className="list-disc pl-5 space-y-2 marker:text-[#00E676]">
                <li>TLS 1.3 on every connection between you and CardWatch</li>
                <li>AES-256 encryption at rest for all financial data</li>
                <li>Plaid connections are read-only and revocable in one click</li>
                <li>
                  Least-privilege access — engineers never see your data unless you open
                  a support ticket that requires it
                </li>
                <li>
                  Regular dependency scanning and security patching of all backend
                  components
                </li>
              </ul>
            </Section>

            <Section id="rights" icon={UserCheck} title="7 · Your rights">
              <p>Under UK/EU GDPR you have the right to:</p>
              <ul className="list-disc pl-5 space-y-2 marker:text-[#00E676]">
                <li>Ask us for a copy of the personal data we hold about you</li>
                <li>Ask us to correct anything that's wrong</li>
                <li>Ask us to delete your data (“right to be forgotten”)</li>
                <li>Withdraw consent or object to processing at any time</li>
                <li>
                  Lodge a complaint with your local supervisory authority (the ICO in the
                  UK)
                </li>
              </ul>
              <p>
                To exercise any of these rights, email us at{" "}
                <InlineTag>privacy@cardwatchapp.com</InlineTag>. We'll respond within 30
                days.
              </p>
            </Section>

            <Section id="contact" icon={Mail} title="8 · Contact">
              <p>
                Questions about this policy or how your data is handled? Get in touch:
              </p>
              <div className="rounded-xl border border-white/5 bg-white/[0.02] p-5 space-y-2">
                <div className="flex items-center gap-2 text-white">
                  <Mail size={14} className="text-[#00E676]" />
                  <a
                    href="mailto:privacy@cardwatchapp.com"
                    className="hover:underline"
                    data-testid="privacy-contact-email"
                  >
                    privacy@cardwatchapp.com
                  </a>
                </div>
                <div className="text-slate-400 text-sm">
                  Or write to us via the address published on{" "}
                  <a
                    href="https://cardwatchapp.com"
                    className="text-[#00E676] hover:underline"
                  >
                    cardwatchapp.com
                  </a>
                  .
                </div>
              </div>
            </Section>

            <p className="text-xs text-slate-500 italic pt-4 border-t border-white/5">
              This policy may be updated from time to time. Material changes will be
              announced via a banner in the app and via email if you have reminders
              enabled.
            </p>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/5 mt-10">
        <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-3">
            <CardWatchMark size={22} />
            <span>© {new Date().getFullYear()} CardWatch. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
